/**
 * PATH: packages/email-hub/src/feature/lib/emailAddress.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-EMAILADDRESS-001
 * PURPOSE: Single source of truth for "is this a syntactically valid email
 *          address?" across the email hub. RecipientAutocomplete gates typed /
 *          pasted / blurred input through this, and the compose send path
 *          re-validates the committed to/cc/bcc arrays as a backstop so a
 *          malformed address seeded by a reply/forward or a resumed draft can
 *          never silently ship (backend 400 / mail bounce) without a clear,
 *          actionable error first.
 * WHY:     Previously the regex lived privately inside RecipientAutocomplete,
 *          so the send path had no way to re-check addresses it didn't itself
 *          create. One shared, tested helper keeps both surfaces in lockstep.
 */

/** Pragmatic address shape: a non-space/@ local part, an @, a domain, a dot,
 *  and a TLD. Matches the rule RecipientAutocomplete has always used to chip a
 *  typed address — deliberately strict enough to catch fat-finger mistakes
 *  (`bob@`, `bob@x`, `bob @x.com`) while accepting every real business
 *  address. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** True when `value` (trimmed) is a syntactically valid email address. */
export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim())
}

/** Return the subset of `addresses` that are NOT valid emails (trimmed,
 *  empties ignored). Used by the send path to name exactly which recipient is
 *  malformed instead of failing opaquely. */
export function invalidEmails(addresses: readonly string[]): string[] {
  const bad: string[] = []
  for (const a of addresses) {
    const t = (a ?? '').trim()
    if (!t) continue
    if (!isValidEmail(t)) bad.push(t)
  }
  return bad
}
